import React, { useId } from 'react';

import { TooltipPortal } from '~components';

/**
 * Props for the ValueRow component.
 * @property {string} title - The title to display in the row.
 * @property {number | string} value - The value to display in the row.
 * @property {string} color - The color used for the row indicator and styling.
 */
interface ValueRowProps {
  title: string;
  value: number | string;
  color: string;
}

/**
 * Formats the value for display, using currency formatting for numbers.
 * @param value - The value to format.
 * @returns {string}
 */
const formatValue = (value: number | string) =>
  typeof value === 'number'
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : value;

/**
 * Renders a single value row with a colored indicator, title, value, and a tooltip.
 *
 * @param {ValueRowProps} props - The props for the component.
 * @returns {JSX.Element} The rendered ValueRow component.
 */
const ValueRow: React.FC<ValueRowProps> = ({ title, value, color }) => {
  const id = useId();
  const rowId = `value-row-${id}`;

  return (
    <>
      <div
        id={rowId}
        className="value-row"
        style={
          {
            '--value-row-colour': color,
          } as React.CSSProperties
        }
      >
        <div className="value-row-title">
          <div className="value-row-color" aria-hidden="true" />
          <span>{title}</span>
        </div>
        <span className="value-row-value">{formatValue(value)}</span>
      </div>
      <TooltipPortal
        targetId={rowId}
        content={
          <span>
            <strong>{title}</strong>: {formatValue(value)}
          </span>
        }
        placement="top"
      />
    </>
  );
};

export default ValueRow;
